import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Search, ClipboardList, ArrowRight, Truck, Handshake, Filter } from "lucide-react";
import MOShell, { useMOAccess, isWritable, LoadingBlock, NoAccessScreen } from "@/components/masterOrder/MOShell";
import StatusBadge from "@/components/masterOrder/StatusBadge";
import {
  listOrders, listClients, BUSINESS_STATUSES, LIFECYCLE_STATUSES, PAYMENT_STATUSES,
  deliveryLabel, paymentLabel, paymentTone, lifecycleLabel,
  displayOrderNumber,
} from "@/lib/masterOrderApi";
import { formatApiError } from "@/lib/api";

function EmptyState({ onCreate, canWrite }) {
  return (
    <div className="rounded-xl bg-white p-10 text-center" style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }} data-testid="mo-orders-empty">
      <div className="mx-auto w-12 h-12 rounded-full grid place-items-center" style={{ background: "#F5EBD8", color: "#8B6F4B" }}>
        <ClipboardList size={20} strokeWidth={1.6} />
      </div>
      <div className="mt-3 text-[20px]" style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14" }}>
        Заказов пока нет
      </div>
      <p className="text-[12.5px] mt-1.5" style={{ color: "#8B6F4B" }}>
        Создайте первый заказ — он появится здесь, а также в кабинете клиента.
      </p>
      {canWrite && (
        <button onClick={onCreate}
                className="mt-5 h-11 px-6 rounded-full text-[13px] inline-flex items-center gap-2"
                style={{ background: "#4A3728", color: "#F7EFE0" }}
                data-testid="mo-orders-empty-cta">
          <Plus size={15} strokeWidth={1.8} /> Новый заказ
        </button>
      )}
    </div>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-");
  return `${d}.${m}.${y}`;
}

export default function MOOrders() {
  const nav = useNavigate();
  const { mode, loading: aLoad } = useMOAccess();
  const canWrite = isWritable(mode);
  const [orders, setOrders] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [businessFilter, setBusinessFilter] = useState("");
  const [lifecycleFilter, setLifecycleFilter] = useState("");
  const [paymentFilter, setPaymentFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [sortDesc, setSortDesc] = useState(false);

  const clientMap = useMemo(() => Object.fromEntries(clients.map((c) => [c.id, c])), [clients]);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      // Backend list_orders only accepts business_status + date range server-side.
      // Lifecycle & payment are filtered client-side against the three separate
      // backend enums (LifecycleStatus / BusinessStatus / PaymentStatus).
      if (businessFilter) params.business_status = businessFilter;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const [ords, cls] = await Promise.all([listOrders(params), listClients()]);
      setOrders(ords);
      setClients(cls);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (!aLoad && mode !== "no_access" && mode !== "demo") load(); /* eslint-disable-line */ }, [aLoad, mode, businessFilter, dateFrom, dateTo]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let out = orders.slice();
    // Client-side lifecycle / payment filters (backend server-side accepts only business_status)
    if (lifecycleFilter) out = out.filter((o) => o.lifecycle_status === lifecycleFilter);
    if (paymentFilter)   out = out.filter((o) => o.payment_status   === paymentFilter);
    if (q) {
      out = out.filter((o) => {
        const cli = clientMap[o.client_id];
        return (
          String(o.order_number).toLowerCase().includes(q) ||
          (cli?.name || "").toLowerCase().includes(q) ||
          (o.wishes || "").toLowerCase().includes(q) ||
          (o.design_notes || "").toLowerCase().includes(q)
        );
      });
    }
    out.sort((a, b) => {
      const ka = `${a.event_date}T${a.event_time || "00:00:00"}`;
      const kb = `${b.event_date}T${b.event_time || "00:00:00"}`;
      return sortDesc ? kb.localeCompare(ka) : ka.localeCompare(kb);
    });
    return out;
  }, [orders, query, clientMap, sortDesc, lifecycleFilter, paymentFilter]);

  return (
    <MOShell active="orders" title="Заказы" subtitle="Все ваши заказы">
      {aLoad ? <LoadingBlock /> : mode === "no_access" || mode === "demo" ? <NoAccessScreen /> : (
        <>
          <div className="flex flex-col md:flex-row md:items-center gap-3 mb-3">
            <div className="flex-1 flex items-center gap-2 h-10 px-4 rounded-full bg-white"
                 style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }}>
              <Search size={14} strokeWidth={1.7} color="#8B6F4B" />
              <input className="flex-1 bg-transparent outline-none text-[13px] placeholder:text-[#B9A692]"
                     placeholder="Поиск: №, клиент, пожелания..."
                     value={query} onChange={(e) => setQuery(e.target.value)}
                     data-testid="mo-orders-search" />
              <span className="text-[11px] tabular-nums" style={{ color: "#8B6F4B" }}>{filtered.length}</span>
            </div>
            {canWrite && (
              <button onClick={() => nav("/master-order/orders/new")}
                      className="h-10 px-5 rounded-full text-[13px] inline-flex items-center gap-2"
                      style={{ background: "#4A3728", color: "#F7EFE0" }}
                      data-testid="mo-orders-new">
                <Plus size={15} strokeWidth={1.8} /> Новый заказ
              </button>
            )}
          </div>

          {/* Filters row — split by backend concept (lifecycle / business / payment) */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2 mb-4" data-testid="mo-orders-filters">
            <div>
              <label className="text-[10.5px] tracking-[0.2em] uppercase" style={{ color: "#8B6F4B" }}>Состояние заказа</label>
              <select value={lifecycleFilter} onChange={(e) => setLifecycleFilter(e.target.value)}
                      className="mt-1 w-full h-9 px-3 rounded-lg text-[12.5px] bg-white outline-none"
                      style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
                      data-testid="mo-orders-lifecycle-filter">
                <option value="">Все</option>
                {LIFECYCLE_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10.5px] tracking-[0.2em] uppercase" style={{ color: "#8B6F4B" }}>Рабочий этап</label>
              <select value={businessFilter} onChange={(e) => setBusinessFilter(e.target.value)}
                      className="mt-1 w-full h-9 px-3 rounded-lg text-[12.5px] bg-white outline-none"
                      style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
                      data-testid="mo-orders-business-filter">
                <option value="">Все</option>
                {BUSINESS_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10.5px] tracking-[0.2em] uppercase" style={{ color: "#8B6F4B" }}>Оплата</label>
              <select value={paymentFilter} onChange={(e) => setPaymentFilter(e.target.value)}
                      className="mt-1 w-full h-9 px-3 rounded-lg text-[12.5px] bg-white outline-none"
                      style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
                      data-testid="mo-orders-payment-filter">
                <option value="">Все</option>
                {PAYMENT_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10.5px] tracking-[0.2em] uppercase" style={{ color: "#8B6F4B" }}>С даты</label>
              <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                     className="mt-1 w-full h-9 px-3 rounded-lg text-[12.5px] bg-white outline-none"
                     style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
                     data-testid="mo-orders-date-from" />
            </div>
            <div>
              <label className="text-[10.5px] tracking-[0.2em] uppercase" style={{ color: "#8B6F4B" }}>По дату</label>
              <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                     className="mt-1 w-full h-9 px-3 rounded-lg text-[12.5px] bg-white outline-none"
                     style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
                     data-testid="mo-orders-date-to" />
            </div>
            <div>
              <label className="text-[10.5px] tracking-[0.2em] uppercase" style={{ color: "#8B6F4B" }}>Сортировка</label>
              <button onClick={() => setSortDesc((v) => !v)}
                      className="mt-1 w-full h-9 px-3 rounded-lg text-[12.5px] bg-white outline-none inline-flex items-center justify-between"
                      style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
                      data-testid="mo-orders-sort">
                <span>{sortDesc ? "Позже → раньше" : "Раньше → позже"}</span>
                <Filter size={13} color="#8B6F4B" />
              </button>
            </div>
          </div>

          {loading ? <LoadingBlock /> : filtered.length === 0 ? (
            <EmptyState canWrite={canWrite} onCreate={() => nav("/master-order/orders/new")} />
          ) : (
            <div className="rounded-xl overflow-hidden bg-white" style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }} data-testid="mo-orders-table">
              <table className="w-full text-[12.5px]">
                <thead style={{ background: "#F5EBD8", color: "#5B4326" }}>
                  <tr>
                    <th className="text-left px-3 py-2 font-normal">№</th>
                    <th className="text-left px-3 py-2 font-normal">Клиент</th>
                    <th className="text-left px-3 py-2 font-normal">Событие</th>
                    <th className="text-left px-3 py-2 font-normal hidden md:table-cell">Получение</th>
                    <th className="text-right px-3 py-2 font-normal">Сумма</th>
                    <th className="text-left px-3 py-2 font-normal">Рабочий этап</th>
                    <th className="text-left px-3 py-2 font-normal hidden lg:table-cell">Оплата</th>
                    <th aria-hidden="true"></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((o) => {
                    const cli = clientMap[o.client_id];
                    const DelIcon = o.delivery?.type && o.delivery.type !== "pickup" ? Truck : Handshake;
                    return (
                      <tr key={o.id}
                          className="border-t hover:bg-[#FBF5EA] cursor-pointer transition"
                          style={{ borderColor: "#EFE4D0" }}
                          onClick={() => nav(`/master-order/orders/${o.id}`)}
                          data-testid={`mo-orders-row-${o.id}`}>
                        <td className="px-3 py-2.5 tabular-nums" style={{ color: "#8B6F4B" }}>
                          № {String(o.order_number || "").replace(/^0+/, "") || "0"}
                          {o.business_status === "cancelled" && (
                            <div className="text-[9.5px] tracking-[0.18em] uppercase mt-0.5" style={{ color: "#8A3F1E" }}>
                              Отменён
                            </div>
                          )}
                          {o.business_status !== "cancelled" && o.lifecycle_status && o.lifecycle_status !== "active" && (
                            <div className="text-[9.5px] tracking-[0.18em] uppercase mt-0.5" style={{ color: "#B9A692" }}>
                              {lifecycleLabel(o.lifecycle_status)}
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2.5" style={{ color: "#2A1D14" }}>{cli?.name || "—"}</td>
                        <td className="px-3 py-2.5 tabular-nums" style={{ color: "#4A3728" }}>
                          {fmtDate(o.event_date)} {o.event_time ? o.event_time.slice(0,5) : ""}
                        </td>
                        <td className="px-3 py-2.5 hidden md:table-cell" style={{ color: "#4A3728" }}>
                          {o.delivery?.type ? (
                            <span className="inline-flex items-center gap-1.5">
                              <DelIcon size={12} strokeWidth={1.7} /> {deliveryLabel(o.delivery.type)}
                            </span>
                          ) : "—"}
                        </td>
                        <td className="px-3 py-2.5 text-right tabular-nums" style={{ color: "#2A1D14" }}>
                          {o.budget ? `${o.budget} ${o.currency_code}` : <span style={{ color: "#B9A692" }}>—</span>}
                        </td>
                        <td className="px-3 py-2.5"><StatusBadge status={o.business_status} /></td>
                        <td className="px-3 py-2.5 hidden lg:table-cell">
                          <StatusBadge tone={paymentTone(o.payment_status)}
                                       testid={`mo-payment-${o.payment_status}`}>
                            {paymentLabel(o.payment_status)}
                          </StatusBadge>
                        </td>
                        <td className="px-3 py-2.5 text-right"><ArrowRight size={13} color="#8B6F4B" /></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </MOShell>
  );
}
